import React, { useContext, useState, useRef } from 'react';
import { HiMiniMagnifyingGlass } from "react-icons/hi2";
import { MdMyLocation } from "react-icons/md";
import * as http from "../../../../utils/http";
import { ChooseTreatmentContext } from '../FreeDosageLayoutV2';
import * as constants from "../constants";
import * as translator from '../../../../utils/translator';
import { useAppContext } from '../../../../providers/AppProvider';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { useCustomer } from '../../../../customHooks/useCustomer';


interface ZipcodeCheckProps {
  resetCart?: () => void;
}

const ZipcodeCheck: React.FC<ZipcodeCheckProps> = ({resetCart}) => {

  const [isExpressNotInRange, setIsExpressNotInRange] = useState(false);
  const { setStickyNotification } = useAppContext();
  const { customerState } = useCustomer();
  const previousZipcodeRef = useRef<string>('');

  const chooseTreatmentContext = useContext(ChooseTreatmentContext);
  const language = chooseTreatmentContext?.language;
  
  if (!chooseTreatmentContext) {
    return null;
  }

  const { products, parseProducts, setProducts, setShippingMethod, customerZipcode, setCustomerZipcode, submitLoading, setSubmitLoading, setStockCodesInRange, setSearchTerm } = chooseTreatmentContext;

  // Helper function to track Changed ZIP Uber event
  const trackChangedZipUber = (newZipcode: string) => {
    if (previousZipcodeRef.current && previousZipcodeRef.current !== newZipcode) {
      MainTracker.track('custom', 'Changed ZIP Uber', { user_id: customerState?.customerID || null, zipcode: newZipcode });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerZipcode.trim() || submitLoading) {
      return;
    }

    const trimmedZipcode = customerZipcode.trim();
    
    // Track Changed_ZIP_Uber event if user edits or re-submits a different ZIP code
    trackChangedZipUber(trimmedZipcode);

    // Track Zipcode_Submitted event (reuse existing event)
    MainTracker.track('custom', 'Zipcode Submitted', { user_id: customerState?.customerID || null, zipcode: trimmedZipcode });

    setSubmitLoading(true);
    
    // Track Uber_Eligibility_Request event
    const requestStartTime = Date.now();
    const requestId = `zip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const zipPrefix = trimmedZipcode.substring(0, 3); // First 3 digits of ZIP
    
    http.postToServer(`choose-treatment/get-express-stocks-by-zipcode`, { zipcode: trimmedZipcode }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
      .then((response) => {
        const latencyMs = Date.now() - requestStartTime;
        
        // Track Uber_Eligibility_Request event after API response
        MainTracker.track('custom', 'Uber_Eligibility_Request', {
          user_id: customerState?.customerID || null,
          zip_prefix: zipPrefix,
          geo_source: 'manual',
          request_id: requestId,
          latency_ms: latencyMs
        });
        
        const expressStocks = response.data.expressStocks;
        const parsedProducts = parseProducts(products, true, expressStocks);
        setProducts(parsedProducts);
        setStockCodesInRange(expressStocks);
        setSearchTerm?.("");
        setIsExpressNotInRange(expressStocks.length === 0);
        
        // Update previous zipcode ref after successful submission
        previousZipcodeRef.current = trimmedZipcode;
        
        // Track Uber_Eligible event (reuse existing event) when express delivery is available
        if (expressStocks.length > 0) {
          MainTracker.track('custom', 'Uber eligable', { 'Pharmacies in range': expressStocks, user_id: customerState?.customerID || null, zipcode: trimmedZipcode });
        } else {
          MainTracker.track('custom', 'Uber not eligable', { user_id: customerState?.customerID || null, zipcode: trimmedZipcode });
        }
        
        resetCart?.();
      })
      .catch((error) => {
        window.clientLogger.error("Error getting express stocks", error);
      })
      .finally(() => {
        // prevent too many calls
        setTimeout(() => {
            setSubmitLoading(false);
        }, 1000);
        
      });
  };

  const handleLocationRequest = () => {
    if (submitLoading) {
      return;
    }
    
    // Track Geo_Click event (user clicks "Use my location" button)
    const userId = customerState?.customerID || null;
    MainTracker.track("custom", "Geo_Click", {
      user_id: userId,
      geo_source: "button",
      screen: "express_shipping"
    });
    
    setSubmitLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Use reverse geocoding to get zipcode from coordinates
            const { latitude, longitude, accuracy } = position.coords;
            
            // Track Geo_Permission_Result event (permission granted)
            MainTracker.track("custom", "Geo_Permission_Result", {
              user_id: userId,
              geo_permission: 'granted',
              lat_lng_precision: accuracy || null,
              geo_source: 'browser'
            });
            
            // Track geo_confirmed event (user confirmed geolocation permission and system received location)
            MainTracker.track("custom", "geo_confirmed", {
              user_id: userId,
              latitude: latitude,
              longitude: longitude,
              geo_source: 'browser'
            });
            
            // Track Uber_Eligibility_Request event - declare variables outside try/catch for scope
            const requestStartTime = Date.now();
            const requestId = `geo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            http.postToServer(`choose-treatment/get-express-stocks-by-long-lat`, { latitude, longitude }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
              .then((response) => {
                const latencyMs = Date.now() - requestStartTime;
                const expressStocks = response.data.expressStocks;
                const zipcode = response.data.zipcode;
                const zipPrefix = zipcode ? zipcode.substring(0, 3) : null; // First 3 digits of ZIP from geolocation
                
                // Track Uber_Eligibility_Request event after API response
                MainTracker.track('custom', 'Uber_Eligibility_Request', {
                  user_id: userId,
                  zip_prefix: zipPrefix,
                  geo_source: 'geo',
                  request_id: requestId,
                  latency_ms: latencyMs
                });
                const parsedProducts = parseProducts(products, true, expressStocks);
                setProducts(parsedProducts);
                setCustomerZipcode(zipcode);
                setStockCodesInRange(expressStocks);
                setSearchTerm?.("");
                setIsExpressNotInRange(expressStocks.length === 0);
                
                // Track Changed_ZIP_Uber event if user uses geolocation and gets a different ZIP code
                trackChangedZipUber(zipcode);
                
                // Update previous zipcode ref after successful geolocation
                previousZipcodeRef.current = zipcode;
                
                // Track Uber_Eligible event (reuse existing event) when express delivery is available
                if (expressStocks.length > 0) {
                  MainTracker.track('custom', 'Uber eligable', { 'Pharmacies in range': expressStocks, user_id: customerState?.customerID || null, zipcode: zipcode });
                } else {
                  MainTracker.track('custom', 'Uber not eligable', { user_id: customerState?.customerID || null, zipcode: zipcode });
                }
                
                resetCart?.();
              })
              .catch((error) => {
                const latencyMs = Date.now() - requestStartTime;
                
                // Track Uber_Eligibility_Request event even on error (to measure API reliability)
                MainTracker.track('custom', 'Uber_Eligibility_Request', {
                  user_id: userId,
                  zip_prefix: null,
                  geo_source: 'geo',
                  request_id: requestId,
                  latency_ms: latencyMs
                });
                
                window.clientLogger.error("Error getting express stocks", error);
              });

          } catch (error) {
            window.clientLogger.error("Error getting express stocks", error);
          } finally {
            // prevent too many calls
            setTimeout(() => {
              setSubmitLoading(false);
            }, 1000);
          };
        },
        (error) => {
          // Track Geo_Permission_Result event (permission denied or other error)
          const geoPermission = error.code === error.PERMISSION_DENIED ? 'denied' : 'denied'; // Treat all errors as denied for tracking
          MainTracker.track("custom", "Geo_Permission_Result", {
            user_id: userId,
            geo_permission: geoPermission,
            lat_lng_precision: null,
            geo_source: 'browser'
          });
          
          let errorMessage = 'Unable to get your location.';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              window.clientLogger.error("Error getting location", errorMessage, error);
          }
          window.clientLogger.info("Error getting location", errorMessage, error);
          setStickyNotification({
            show: true,
            title: translator.translate(language, "choose_treatment", "default/error_getting_location"),
            content: translator.translate(language, "choose_treatment", "default/error_getting_location_description"),
            level: "error",
            showInfoIcon: false,
            autoFade: true,
          });
          setSubmitLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-2 border border-solid bg-white border-[#269C6E] rounded-md p-3">
      <div className="flex flex-row gap-3 justify-between">

        {/* Input Section */}
        <div className="flex w-1/2 h-14 p-2 gap-2 relative justify-between border border-solid border-[#E5E5E5] rounded-md">

            <input
              type="text"
              name="zipcode"
              inputMode="numeric"
              pattern="[0-9]*"
              value={customerZipcode}
              onChange={(e) => {
                setCustomerZipcode(e.target.value.replace(/[^0-9]/g, ""));
              }}
              placeholder="Postleitzahl"
              maxLength={5}
              autoComplete="off" data-1p-ignore
              className="flex-1 w-full h-full px-3 text-[16px] border-none rounded-md bg-white text-[#0D2C54] font-roboto focus:outline-none focus:border-[#06D1A1] focus:ring-1 focus:ring-[#06D1A1]"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !submitLoading) {
                    handleSubmit(e);
                }
              }}
            />

            <button
              onClick={handleSubmit}
              className={`w-10 h-10 bg-[#269C6E] border-0 ${submitLoading ? 'cursor-wait' : 'cursor-pointer'} outline-none text-white rounded-md text-sm flex items-center justify-center`}
            >
                <HiMiniMagnifyingGlass className="w-6 h-6" />
            </button>

        </div>

        {/* Location Button Section */}
        <div className="flex w-1/2 items-center gap-2 ">
          <button
            onClick={handleLocationRequest}
            disabled={submitLoading}
            className="flex items-center justify-center gap-2 h-14 w-full cursor-pointer bg-white border border-solid border-[#269C6E] rounded-md p-3 font-roboto text-sm hover:opacity-80 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <MdMyLocation className="w-6 h-6 text-[#269C6E]" />
            <span className="text-[#269C6E] font-poppins text-[11px] md:text-[13px]">{translator.translate(language, "choose_treatment", "default/use_current_location")}</span>
          </button>
        </div>
      </div>
      {
        isExpressNotInRange ? (
          <div className="w-full flex flex-col items-center justify-center mt-2 mb-2">
            <p className="text-xl font-roboto font-bold text-center" id="zipcode-check-unavailable-title">
              {translator.translate(language, "choose_treatment", "shipping_and_service_selection/express_delivery_not_available_title")}
            </p>
            <p className="text-[#98A0AF] font-roboto text-xs text-center my-2" id="zipcode-check-unavailable-desc">
              {translator.translate(language, "choose_treatment", "shipping_and_service_selection/express_delivery_not_available_description")}
            </p>
            <div className="flex flex-row w-full gap-3 mt-1">
              <button
                className="flex items-center justify-center text-[#269C6E] text-lg font-semibold gap-2 h-14 w-full cursor-pointer bg-white border-1 border-solid border-[#269C6E] rounded-md p-3 font-roboto hover:opacity-80"
                onClick={() => {
                  const zipPrefix = customerZipcode && customerZipcode.length >= 3 ? customerZipcode.substring(0, 3) : null;
                  MainTracker.track('custom', 'Uber_Ineligible_CTA_Clicked', {
                    user_id: customerState?.customerID || null,
                    choice: 'switch_to_48h',
                    zip_prefix: zipPrefix
                  });
                  setShippingMethod(constants.SHIPPING);
                  resetCart?.();
                }}
              >
                {translator.translate(language, "choose_treatment", "shipping_and_service_selection/shipping")}
              </button>
              <button
                className="flex items-center justify-center text-[#269C6E] text-lg font-semibold gap-2 h-14 w-full cursor-pointer bg-white border-1 border-solid border-[#269C6E] rounded-md p-3 font-roboto hover:opacity-80"
                onClick={() => {
                  const zipPrefix = customerZipcode && customerZipcode.length >= 3 ? customerZipcode.substring(0, 3) : null;
                  MainTracker.track('custom', 'Uber_Ineligible_CTA_Clicked', {
                    user_id: customerState?.customerID || null,
                    choice: 'switch_to_pickup',
                    zip_prefix: zipPrefix
                  });
                  setShippingMethod(constants.PICKUP);
                  resetCart?.();
                }}
              >
                {translator.translate(language, "choose_treatment", "shipping_and_service_selection/pickup")}
              </button>
            </div>
          </div>
        ) : (
          <p id="zipcode-check-description" className="text-[#98A0AF] font-roboto text-xs">
            {translator.translate(language, "choose_treatment", "default/zipcode_check_description")}
          </p>
        )
      }
    </div>
  );
};

export default ZipcodeCheck;
